/* ==========================================================================
   hp-product.js
   Behaviour for the new Honeypot product page.

   Deliberately self-contained: it defines its own custom elements with an
   `hp-` prefix and does not touch, extend or depend on any existing theme
   script (VariantTools.js, media-gallery.js, product-info.js and friends
   are all left alone).

   One intentional handshake with the old code: <hp-media-gallery> is given
   the id `MediaGallery-{section.id}` and exposes a `setActiveMedia()`
   method, because the theme's VariantTools.js looks up any element whose
   id starts with `MediaGallery-` and calls that method when a variant
   changes. Implementing it here means variant image switching keeps
   working without editing VariantTools.js.
   ========================================================================== */

(function () {
  'use strict';

  var money = function (cents, format) {
    if (typeof cents !== 'number') return '';
    var value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, value);
  };

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-media-gallery')) {
    customElements.define('hp-media-gallery', class HpMediaGallery extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-hp-grid]');
        this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-hp-tile]'));
        this.dotsWrap = this.querySelector('[data-hp-dots]');
        this.buildDots();
        this.bindZoom();
        this.bindScroll();
      }

      buildDots() {
        if (!this.dotsWrap || this.tiles.length < 2) return;
        var self = this;
        this.dots = this.tiles.map(function (tile, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'hp-gallery__dot';
          dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
          dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          dot.addEventListener('click', function () {
            tile.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
          self.dotsWrap.appendChild(dot);
          return dot;
        });
      }

      bindScroll() {
        if (!this.grid || !this.dots) return;
        var self = this;
        var raf = null;
        this.grid.addEventListener('scroll', function () {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = null;
            var mid = self.grid.scrollLeft + self.grid.clientWidth / 2;
            var index = 0;
            var best = Infinity;
            self.tiles.forEach(function (tile, i) {
              var centre = tile.offsetLeft + tile.offsetWidth / 2;
              var delta = Math.abs(centre - mid);
              if (delta < best) { best = delta; index = i; }
            });
            self.dots.forEach(function (dot, i) {
              dot.setAttribute('aria-current', i === index ? 'true' : 'false');
            });
          });
        }, { passive: true });
      }

      bindZoom() {
        var self = this;
        this.addEventListener('click', function (event) {
          var trigger = event.target.closest('[data-hp-zoom]');
          if (!trigger) return;
          event.preventDefault();
          var box = document.getElementById(self.dataset.lightbox);
          if (box && box.openAt) box.openAt(trigger.dataset.hpZoom);
        });
      }

      /* Called by the theme's VariantTools.js on variant change. */
      setActiveMedia(mediaKey) {
        if (!mediaKey) return;
        var id = String(mediaKey).split('-').pop();
        var tile = this.querySelector('[data-media-id="' + id + '"]');
        if (!tile) return;
        // Promote the variant's image to the hero slot so it reads first.
        if (this.grid && tile.parentNode === this.grid && tile !== this.grid.firstElementChild) {
          this.tiles.forEach(function (t) { t.classList.remove('hp-gallery__tile--hero'); });
          this.grid.insertBefore(tile, this.grid.firstElementChild);
          tile.classList.add('hp-gallery__tile--hero');
        }
        if (window.matchMedia('(max-width: 749px)').matches) {
          tile.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Lightbox                                                            */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-lightbox')) {
    customElements.define('hp-lightbox', class HpLightbox extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.scroll = this.querySelector('[data-hp-lightbox-scroll]');
        this.addEventListener('click', function (event) {
          if (event.target.closest('[data-hp-lightbox-close]') || event.target === self.scroll) {
            self.close();
          }
        });
        this.onKey = function (event) { if (event.key === 'Escape') self.close(); };
      }

      openAt(mediaId) {
        this.setAttribute('open', '');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKey);
        var target = mediaId && this.querySelector('[data-media-id="' + mediaId + '"]');
        if (target) target.scrollIntoView({ block: 'center' });
        var close = this.querySelector('[data-hp-lightbox-close]');
        if (close) close.focus();
      }

      close() {
        this.removeAttribute('open');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKey);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Accordion                                                           */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-accordion')) {
    customElements.define('hp-accordion', class HpAccordion extends HTMLElement {
      connectedCallback() {
        this.head = this.querySelector('[data-hp-acc-head]');
        this.panel = this.querySelector('[data-hp-acc-panel]');
        if (!this.head || !this.panel) return;
        var self = this;

        if (this.hasAttribute('open')) {
          this.head.setAttribute('aria-expanded', 'true');
          this.panel.style.height = 'auto';
        }

        this.head.addEventListener('click', function () {
          var open = self.head.getAttribute('aria-expanded') === 'true';
          open ? self.collapse() : self.expand();
        });
      }

      expand() {
        var inner = this.panel.firstElementChild;
        this.head.setAttribute('aria-expanded', 'true');
        this.panel.style.height = inner.offsetHeight + 'px';
        var panel = this.panel;
        panel.addEventListener('transitionend', function done() {
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', done);
        });
      }

      collapse() {
        var inner = this.panel.firstElementChild;
        this.head.setAttribute('aria-expanded', 'false');
        this.panel.style.height = inner.offsetHeight + 'px';
        void this.panel.offsetHeight;
        this.panel.style.height = '0px';
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-cart button price                                            */
  /*                                                                     */
  /* The figure in the button is unit price x quantity, so it has to be  */
  /* recalculated from two independent inputs: the variant picker moves  */
  /* the unit price, the stepper moves the quantity. Keeping the unit    */
  /* price on the button as a data attribute means neither needs to know */
  /* about the other — each just writes its half and calls this.         */
  /* ------------------------------------------------------------------ */
  function hpSyncAtcPrice(sectionId) {
    var button = document.getElementById('hpSubmit-' + sectionId);
    if (!button) return;

    var out = document.querySelector('[data-hp-atc-price="' + sectionId + '"]');
    if (!out) return;

    var unit = parseInt(button.getAttribute('data-hp-unit-price') || '0', 10);
    var qtyInput = document.getElementById('HpQty-' + sectionId);

    /*  Quantity defaults to 1 rather than 0 when the stepper is switched off
        in the editor, otherwise the button would read the currency symbol
        followed by nothing. */
    var qty = 1;
    if (qtyInput) {
      qty = parseInt(qtyInput.value, 10);
      var min = parseInt(qtyInput.min || '1', 10) || 1;
      if (!qty || qty < min) qty = min;
    }

    out.textContent = money(unit * qty, window.hpMoneyFormat || '{{amount}}');
  }

  /*  Exposed so the sticky bar, or anything else that ends up mirroring the
      buy row, can trigger a recalculation without duplicating the maths. */
  window.hpSyncAtcPrice = hpSyncAtcPrice;

  /* ------------------------------------------------------------------ */
  /* Quantity stepper                                                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-qty')) {
    customElements.define('hp-qty', class HpQty extends HTMLElement {
      connectedCallback() {
        var input = this.querySelector('input');
        if (!input) return;

        /*  The section id is not on this element, so it is read off the input
            id the snippet already prints: HpQty-{section.id}. */
        var sectionId = (input.id || '').replace(/^HpQty-/, '');

        this.addEventListener('click', function (event) {
          var btn = event.target.closest('button[data-hp-step]');
          if (!btn) return;
          var step = parseInt(input.step || '1', 10) || 1;
          var min = parseInt(input.min || '1', 10) || 1;
          var max = input.max ? parseInt(input.max, 10) : Infinity;
          var next = (parseInt(input.value, 10) || min) + (btn.dataset.hpStep === 'up' ? step : -step);
          input.value = Math.min(max, Math.max(min, next));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        /*  Covers both the stepper buttons (which fire change above) and the
            customer typing a number straight into the field. */
        input.addEventListener('input', function () { hpSyncAtcPrice(sectionId); });
        input.addEventListener('change', function () { hpSyncAtcPrice(sectionId); });

        hpSyncAtcPrice(sectionId);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-cart form                                                    */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-product-form')) {
    customElements.define('hp-product-form', class HpProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[type="submit"]');
        this.errorBox = this.querySelector('[data-hp-form-error]');
        if (!this.form) return;

        var idInput = this.form.querySelector('[name="id"]');
        if (idInput) idInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(event) {
        if (this.dataset.ajax === 'false') return;
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        var self = this;
        this.setError('');
        this.button.classList.add('hp-is-loading');
        this.button.setAttribute('aria-disabled', 'true');

        var drawer = document.querySelector('cart-drawer');
        var body = new FormData(this.form);

        /*  Shopify only returns rendered section HTML when the request asks
            for it by name. Sending sections_url on its own was the bug behind
            the empty drawer: the POST succeeded, data.sections came back
            undefined, so renderContents was skipped and the drawer opened
            still showing the markup from page load — which on a fresh visit
            is the empty state. Reloading appeared to fix it only because the
            new page render fetched a current cart.

            The section ids come from the drawer itself rather than being
            hardcoded, so this keeps matching whatever cart.js decides to
            render (currently cart-drawer and cart-icon-bubble). */
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.append(
            'sections',
            drawer.getSectionsToRender().map(function (section) { return section.id; }).join(',')
          );
        }
        body.append('sections_url', window.location.pathname);

        /*  Matches the theme's own product-form: it tells Shopify this is an
            XHR, and cart.js branches on it in places. */
        fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: body
        })
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (data.status) {
              self.setError(data.description || data.message || 'Could not add to cart.');
              return;
            }
            document.dispatchEvent(new CustomEvent('hp:cart:added', { detail: data }));

            if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
              /*  The drawer keeps an is-empty class from the last render. Left
                  in place it hides the item list even once the new HTML is in,
                  which is the second half of the same empty-cart symptom. */
              drawer.classList.remove('is-empty');
              var inner = drawer.querySelector('.drawer__inner');
              if (inner) inner.classList.remove('is-empty');

              if (typeof drawer.setActiveElement === 'function') {
                drawer.setActiveElement(self.button);
              }
              /*  renderContents opens the drawer itself once the sections are
                  swapped in, so there is no open() call here. */
              drawer.renderContents(data);
            } else if (drawer && typeof drawer.open === 'function') {
              drawer.open();
            } else {
              window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch(function () { self.setError('Something went wrong. Please try again.'); })
          .finally(function () {
            self.button.classList.remove('hp-is-loading');
            self.button.removeAttribute('aria-disabled');
          });
      }

      setError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message;
        this.errorBox.hidden = !message;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                      */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-variant-picker')) {
    customElements.define('hp-variant-picker', class HpVariantPicker extends HTMLElement {
      connectedCallback() {
        var script = this.querySelector('[data-hp-variants]');
        this.variants = script ? JSON.parse(script.textContent) : [];
        this.addEventListener('change', this.onChange.bind(this));
        this.syncAvailability();
      }

      get selectedOptions() {
        return Array.prototype.map.call(
          this.querySelectorAll('[data-hp-option-group]'),
          function (group) {
            var checked = group.querySelector('input:checked');
            return checked ? checked.value : null;
          }
        );
      }

      onChange() {
        var chosen = this.selectedOptions;
        var variant = this.variants.find(function (v) {
          return v.options.every(function (opt, i) { return chosen[i] === opt; });
        });

        // Reflect the chosen value next to each option label.
        Array.prototype.forEach.call(this.querySelectorAll('[data-hp-option-group]'), function (group) {
          var checked = group.querySelector('input:checked');
          var out = group.querySelector('[data-hp-option-value]');
          if (checked && out) out.textContent = checked.value;
        });

        this.syncAvailability();
        document.dispatchEvent(new CustomEvent('hp:variant:change', {
          detail: { variant: variant, sectionId: this.dataset.section }
        }));

        if (!variant) { this.setUnavailable(); return; }

        this.updateFormInputs(variant);
        this.updateUrl(variant);
        this.updatePrice(variant);
        this.updateGallery(variant);
        this.updateFabricCard();
      }

      setUnavailable() {
        var button = document.getElementById('hpSubmit-' + this.dataset.section);
        if (!button) return;
        button.disabled = true;
        var label = button.querySelector('[data-hp-atc-label]');
        if (label) label.textContent = window.hpStrings ? window.hpStrings.unavailable : 'Unavailable';
      }

      updateFormInputs(variant) {
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-hp-variant-input="' + this.dataset.section + '"]'),
          function (input) {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        );

        var button = document.getElementById('hpSubmit-' + this.dataset.section);
        if (button) {
          button.disabled = !variant.available;
          var label = button.querySelector('[data-hp-atc-label]');
          if (label) {
            label.textContent = variant.available
              ? (window.hpStrings ? window.hpStrings.addToCart : 'Add to cart')
              : (window.hpStrings ? window.hpStrings.soldOut : 'Sold out');
          }

          /*  "Sold out | AED 2,400" reads as a price you can still pay, so the
              figure and its divider come off until the variant is buyable
              again. */
          var section = this.dataset.section;
          Array.prototype.forEach.call(
            document.querySelectorAll(
              '[data-hp-atc-price="' + section + '"], [data-hp-atc-divider="' + section + '"]'
            ),
            function (el) { el.hidden = !variant.available; }
          );
        }
      }

      updateUrl(variant) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
      }

      updatePrice(variant) {
        var format = window.hpMoneyFormat || '{{amount}}';
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-hp-price-now="' + this.dataset.section + '"]'),
          function (el) { el.textContent = money(variant.price, format); }
        );

        /*  The button carries the single-item price and derives its own total,
            so only the unit figure is written here. Calling the sync straight
            after keeps the multiplier applied when the customer changes colour
            with a quantity of 3 already set. */
        var button = document.getElementById('hpSubmit-' + this.dataset.section);
        if (button) {
          button.setAttribute('data-hp-unit-price', variant.price);
          if (typeof window.hpSyncAtcPrice === 'function') {
            window.hpSyncAtcPrice(this.dataset.section);
          }
        }
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-hp-price-was="' + this.dataset.section + '"]'),
          function (el) {
            var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
            el.textContent = onSale ? money(variant.compare_at_price, format) : '';
            el.hidden = !onSale;
          }
        );
      }

      updateGallery(variant) {
        if (!variant.featured_media) return;
        var gallery = document.getElementById('MediaGallery-' + this.dataset.section);
        if (gallery && gallery.setActiveMedia) {
          gallery.setActiveMedia(this.dataset.section + '-' + variant.featured_media.id);
        }
      }

      updateFabricCard() {
        var card = this.querySelector('[data-hp-fabric-card]');
        if (!card) return;
        var group = this.querySelector('[data-hp-option-group][data-hp-is-fabric="true"]');
        var checked = group && group.querySelector('input:checked');
        if (!checked) return;
        var key = checked.value.trim().toLowerCase();
        Array.prototype.forEach.call(card.querySelectorAll('[data-hp-fabric-panel]'), function (panel) {
          panel.hidden = panel.dataset.hpFabricPanel !== key;
        });

        /*  The anchor row at the foot of the card names the selected fabric,
            so it has to move with the pills or it goes stale the moment
            someone switches — showing "Classic" above "Performance" bullets. */
        Array.prototype.forEach.call(card.querySelectorAll('[data-hp-fabric-value]'), function (el) {
          el.textContent = checked.value;
        });
      }

      /* Grey out combinations that do not resolve to a purchasable variant. */
      syncAvailability() {
        var self = this;
        var chosen = this.selectedOptions;
        Array.prototype.forEach.call(this.querySelectorAll('[data-hp-option-group]'), function (group, index) {
          Array.prototype.forEach.call(group.querySelectorAll('input'), function (input) {
            var trial = chosen.slice();
            trial[index] = input.value;
            var exists = self.variants.some(function (v) {
              return v.available && v.options.every(function (opt, i) {
                return i === index ? trial[i] === opt : (trial[i] == null || trial[i] === opt);
              });
            });
            input.classList.toggle('hp-is-unavailable', !exists);
          });
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Clamped description toggle                                          */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-hp-notes-toggle]');
    if (!btn) return;
    var body = document.getElementById(btn.dataset.hpNotesToggle);
    if (!body) return;
    var clamped = body.classList.toggle('hp-is-clamped');
    btn.textContent = clamped ? btn.dataset.hpMore : btn.dataset.hpLess;
  });

  /* ------------------------------------------------------------------ */
  /* Mobile sticky bar                                                   */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('hp-sticky-bar')) {
    customElements.define('hp-sticky-bar', class HpStickyBar extends HTMLElement {
      connectedCallback() {
        var anchor = document.getElementById(this.dataset.watch);
        if (!anchor || !('IntersectionObserver' in window)) return;
        var self = this;
        new IntersectionObserver(function (entries) {
          self.classList.toggle('hp-is-visible', !entries[0].isIntersecting);
        }, { rootMargin: '0px 0px -60px 0px' }).observe(anchor);
      }
    });
  }
})();

/* ==========================================================================
   Lightbox portal
   --------------------------------------------------------------------------
   The lightbox is `position: fixed`, but fixed positioning is relative to the
   nearest ancestor that establishes a containing block — and `position:
   sticky` establishes one. The gallery column is sticky, so the lightbox was
   trapped inside it: the info column painted over the image, and the site
   header sat above it, because a z-index inside a stacking context can never
   escape that context no matter how large it is.

   Raising z-index cannot fix this. The element has to physically leave.
   On first run it is moved to <body>, where it is a sibling of the header and
   the ordinary z-index ordering applies.
   ========================================================================== */
(function () {
  function portal() {
    document.querySelectorAll('.hp-lightbox').forEach(function (lb) {
      if (lb.parentNode !== document.body) document.body.appendChild(lb);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', portal);
  } else {
    portal();
  }
  document.addEventListener('shopify:section:load', portal);

  /*  Scroll lock. Without it the page behind keeps scrolling under the
      overlay, and on iOS the address bar collapses mid-gesture.          */
  var scrollY = 0;
  function lock() {
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
    document.body.classList.add('hp-lb-open');
  }
  function unlock() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove('hp-lb-open');
    window.scrollTo(0, scrollY);
  }
  /*  Watch ONLY lightboxes. `open` is not unique to this element — the
      accordions are <hp-accordion open> and use the very same attribute, so
      an unfiltered observer locked the page every time somebody expanded
      "Delivery & returns" and there was no lightbox to close to undo it.   */
  new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      if (m.attributeName !== 'open') return;
      var el = m.target;
      if (!el.classList || !el.classList.contains('hp-lightbox')) return;
      el.hasAttribute('open') ? lock() : unlock();
    });
  }).observe(document.documentElement, {
    subtree: true, attributes: true, attributeFilter: ['open']
  });

  /*  Safety valve. If a lock is ever left behind — a lightbox removed from
      the DOM while open, a section re-render mid-overlay — the page would be
      frozen with no way back. Escape always releases it.                   */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (!document.querySelector('.hp-lightbox[open]')) unlock();
  });
})();

/* ==========================================================================
   Dual sticky columns
   --------------------------------------------------------------------------
   Both columns stick. Whichever is shorter finishes first and parks at its own
   end while the taller one carries on; when the taller one is done the page
   resumes normal flow. Symmetric, and it re-derives itself whenever a column
   changes height.

   Everything hinges on one value per column:

     fits on screen    ->  top = header offset          (ordinary sticky)
     taller than that  ->  top = viewportHeight - height (negative, so the
                           column pins when its BOTTOM meets the viewport
                           bottom — after you have read all of it)

   Neither can be expressed in plain CSS because both depend on the element's
   own height, so it is written here as --hp-col-top and recomputed on resize
   and on any height change.

   When a pinned column grows because an accordion opened, its pin point moves
   up by the same amount its height grew, so its bottom edge stays against the
   bottom of the viewport and the newly revealed content appears exactly where
   the user is already looking. That is why there is no scroll compensation
   here: compensating would push the new content back off-screen.
   ========================================================================== */
(function () {
  var MIN_WIDTH = 990;      // below this the columns are stacked
  var controllers = [];

  function StickyColumns(root) {
    this.root = root;
    this.cols = [
      root.querySelector(':scope > .hp-media'),
      root.querySelector(':scope > .hp-info')
    ].filter(Boolean);
    this.frame = null;
    this.observers = [];
    if (this.cols.length !== 2) return;
    this.bind();
    this.measure();
  }

  StickyColumns.prototype.offset = function () {
    var raw = getComputedStyle(this.root).getPropertyValue('--hp-sticky-offset');
    var n = parseFloat(raw);
    return isNaN(n) ? 24 : n;
  };

  /*  Gap left between a pinned column's bottom edge and the bottom of the
      viewport. Without it the column parks flush against the screen edge,
      which reads as the content being cut off rather than resting.        */
  StickyColumns.prototype.bottomGap = function () {
    var raw = getComputedStyle(this.root).getPropertyValue('--hp-sticky-bottom');
    var n = parseFloat(raw);
    return isNaN(n) ? 13 : n;
  };

  StickyColumns.prototype.apply = function () {
    this.frame = null;

    // Stacked layout: hand control back to normal flow.
    if (window.innerWidth < MIN_WIDTH) {
      this.cols.forEach(function (c) { c.style.removeProperty('--hp-col-top'); });
      return;
    }

    var vh = window.innerHeight;
    var offset = this.offset();
    var gap = this.bottomGap();

    this.cols.forEach(function (col) {
      var h = col.offsetHeight;
      // A column that fits pins at the offset. One that does not pins so its
      // bottom edge sits `gap` above the bottom of the viewport.
      var top = (h + offset + gap <= vh) ? offset : vh - h - gap;
      col.style.setProperty('--hp-col-top', Math.round(top) + 'px');
    });
  };

  StickyColumns.prototype.measure = function () {
    if (this.frame) return;                  // coalesce bursts into one frame
    this.frame = requestAnimationFrame(this.apply.bind(this));
  };

  StickyColumns.prototype.bind = function () {
    var self = this;
    var remeasure = this.measure.bind(this);

    window.addEventListener('resize', remeasure, { passive: true });
    window.addEventListener('orientationchange', remeasure, { passive: true });

    // Height changes: accordions opening, images finishing load, variant
    // swaps rewriting the price, apps injecting a widget.
    if (typeof ResizeObserver === 'function') {
      this.cols.forEach(function (col) {
        var ro = new ResizeObserver(remeasure);
        ro.observe(col);
        self.observers.push(ro);
      });
    } else {
      // Old Safari: re-check after transitions, which is when accordions settle.
      this.root.addEventListener('transitionend', remeasure);
    }

    // Fonts land late and change text height.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(remeasure).catch(function () {});
    }
    window.addEventListener('load', remeasure, { passive: true });
  };

  StickyColumns.prototype.destroy = function () {
    this.observers.forEach(function (o) { o.disconnect(); });
    this.observers = [];
    if (this.frame) cancelAnimationFrame(this.frame);
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-hp-sticky-columns]').forEach(function (el) {
      if (el.__hpSticky) return;
      el.__hpSticky = new StickyColumns(el);
      controllers.push(el.__hpSticky);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-hp-sticky-columns]');
    if (el && el.__hpSticky) { el.__hpSticky.destroy(); el.__hpSticky = null; }
  });
})();

/* ==========================================================================
   Selling point slider  <hp-usp-slider>
   --------------------------------------------------------------------------
   Rotates one slide at a time with dots, swipe and keyboard.

   Autoplay pauses when the strip is off-screen, when the tab is hidden, on
   hover, and on keyboard focus. A carousel that keeps cycling while nobody
   is looking burns battery and moves under the cursor the moment someone
   arrives; pausing on focus stops it stealing the slide out from under
   somebody tabbing through the dots.

   prefers-reduced-motion short-circuits the whole thing: the CSS shows every
   slide stacked, so there is nothing to rotate and no timer is started.
   ========================================================================== */
(function () {
  if (customElements.get('hp-usp-slider')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function UspSlider() { return Reflect.construct(HTMLElement, [], UspSlider); }
  UspSlider.prototype = Object.create(HTMLElement.prototype);
  UspSlider.prototype.constructor = UspSlider;
  Object.setPrototypeOf(UspSlider, HTMLElement);

  UspSlider.prototype.connectedCallback = function () {
    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-usp-slide]'));
    this.dots   = Array.prototype.slice.call(this.querySelectorAll('[data-usp-dot]'));
    this.index  = 0;
    this.timer  = null;
    this.paused = false;

    if (!this.slides.length) return;
    this.show(0);

    if (this.slides.length < 2 || REDUCED.matches) return;

    this.delay = parseInt(this.getAttribute('data-autoplay'), 10) || 0;
    this.bind();
    if (this.delay) this.start();
  };

  UspSlider.prototype.disconnectedCallback = function () { this.stop(); };

  UspSlider.prototype.show = function (i) {
    var n = this.slides.length;
    var prev = this.index;
    this.index = (i % n + n) % n;                     // wraps both directions
    if (prev === this.index && this.slides[prev].hasAttribute('data-active')) return;

    for (var k = 0; k < n; k++) {
      var on = k === this.index;

      /*  The slide being replaced is marked `data-leaving` so it exits to the
          left. Without it CSS would return it to its resting position on the
          right, and the pair would look like they were passing each other in
          opposite directions.                                              */
      if (k === prev && !on) {
        this.slides[k].setAttribute('data-leaving', '');
      } else {
        this.slides[k].removeAttribute('data-leaving');
      }

      this.slides[k].toggleAttribute('data-active', on);
      // aria-hidden keeps a screen reader off the slides it cannot see
      this.slides[k].setAttribute('aria-hidden', on ? 'false' : 'true');
      if (this.dots[k]) {
        if (on) this.dots[k].setAttribute('aria-current', 'true');
        else    this.dots[k].removeAttribute('aria-current');
      }
    }
  };

  UspSlider.prototype.next = function () { this.show(this.index + 1); };
  UspSlider.prototype.prev = function () { this.show(this.index - 1); };

  UspSlider.prototype.start = function () {
    var self = this;
    this.stop();
    if (!this.delay || this.paused) return;
    this.timer = setInterval(function () { self.next(); }, this.delay);
  };
  UspSlider.prototype.stop = function () {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  };
  UspSlider.prototype.pause = function () { this.paused = true;  this.stop();  };
  UspSlider.prototype.play  = function () { this.paused = false; this.start(); };

  UspSlider.prototype.bind = function () {
    var self = this;

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { self.show(i); self.start(); });
    });

    ['mouseenter', 'focusin'].forEach(function (e) {
      self.addEventListener(e, function () { self.pause(); });
    });
    ['mouseleave', 'focusout'].forEach(function (e) {
      self.addEventListener(e, function () { self.play(); });
    });

    this.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.next(); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); self.prev(); }
    });

    // Swipe. 40px threshold, and the horizontal move must beat the vertical
    // one so a normal page scroll is never read as a swipe.
    var x0 = null, y0 = null;
    this.addEventListener('touchstart', function (e) {
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    this.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? self.next() : self.prev();
        self.start();
      }
      x0 = y0 = null;
    }, { passive: true });

    // Off-screen and background tabs: no reason to keep a timer running.
    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? self.play() : self.pause(); });
      }, { threshold: 0 }).observe(this);
    }
    document.addEventListener('visibilitychange', function () {
      document.hidden ? self.pause() : self.play();
    });
  };

  customElements.define('hp-usp-slider', UspSlider);
})();